import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuthStore, hasMinimumRole, ROLES } from '../lib/auth';
import { taskAPI, venueAPI, userAPI } from '../lib/api';
import { supabase } from '../supabaseClient'; // REAL Supabase client restored
import { 
  AlertTriangle, 
  Clock,
  CheckCircle2,
  User,
  Building2,
  Loader2,
  AlertCircle,
  Timer,
  Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const TaskCard = ({ task, onUpdate, users }) => {
  const { user: currentUser } = useAuthStore();
  const [updating, setUpdating] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [overrideNote, setOverrideNote] = useState('');
  const [isSubmittingOverride, setIsSubmittingOverride] = useState(false);

  const getPriorityStyles = (priority) => {
    const styles = {
      high: { bg: 'bg-[#FF3B30]/20', text: 'text-[#FF3B30]', border: 'border-[#FF3B30]/50' },
      medium: { bg: 'bg-[#FFB300]/20', text: 'text-[#FFB300]', border: 'border-[#FFB300]/50' },
      low: { bg: 'bg-[#00E676]/20', text: 'text-[#00E676]', border: 'border-[#00E676]/50' },
    };
    return styles[priority] || styles.medium;
  };

  const getStatusStyles = (status) => {
    const styles = {
      pending: { bg: 'bg-[#FFB300]', text: 'PENDING' },
      in_progress: { bg: 'bg-[#2979FF]', text: 'IN PROGRESS' },
      completed: { bg: 'bg-[#00E676]', text: 'COMPLETED' },
      overdue: { bg: 'bg-[#FF3B30]', text: 'OVERDUE' },
      escalated: { bg: 'bg-[#FF3B30]', text: 'ESCALATED' },
    };
    return styles[status] || styles.pending;
  };

  const getTimeRemaining = () => {
    if (!task.deadline) return { text: 'No Deadline', isOverdue: false };
    const deadline = new Date(task.deadline);
    const now = new Date();
    const diff = deadline - now;
    
    if (diff <= 0) return { text: 'Overdue', isOverdue: true };
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return { text: `${days}d ${hours % 24}h`, isOverdue: false };
    }
    
    return { text: `${hours}h ${minutes}m`, isOverdue: hours < 2 };
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await taskAPI.update(task.id, { status: newStatus });
      toast.success('Task status updated');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update task');
    } finally {
      setUpdating(false);
    }
  };

  const handleAssign = async (userId) => {
    setUpdating(true);
    try {
      await taskAPI.update(task.id, { assigned_to: userId });
      toast.success('Task assigned');
      setShowAssign(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to assign task');
    } finally {
      setUpdating(false);
    }
  };

  const handleOpsOverride = async (e) => {
    e.preventDefault();
    if (!overrideNote.trim()) return;
    setIsSubmittingOverride(true);

    try {
      // Safely insert into real Supabase instance
      const { error: incidentError } = await supabase
        .from('incident_reports')
        .insert({
          title: `Overdue Task Overridden: ${task.title}`,
          description: `Ops Override Note: ${overrideNote}`,
          type: 'task_escalation',
          severity: 'medium',
          venue_id: task.venue_id
        });

      if (incidentError) throw incidentError;

      const newDeadline = new Date(Date.now() + 86400000).toISOString();
      await taskAPI.update(task.id, { 
        status: 'pending',
        deadline: newDeadline
      });

      toast.success('Task rescheduled and incident logged.');
      setOverrideNote('');
      onUpdate();
    } catch (err) {
      console.error("Failed to process override", err);
      toast.error('Failed to process Ops Override. Check database connection.');
    } finally {
      setIsSubmittingOverride(false);
    }
  };

  const priority = getPriorityStyles(task.priority);
  const status = getStatusStyles(task.status);
  const timeRemaining = getTimeRemaining();
  const isComplete = task.status === 'completed';
  const isOverdue = timeRemaining.isOverdue;

  return (
    <div className={`card-rtb border-l-4 ${priority.border} ${isComplete ? 'opacity-60' : ''}`} data-testid={`task-${task.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-md ${priority.bg}`}>
            <AlertTriangle className={`w-5 h-5 ${priority.text}`} />
          </div>
          <div>
            <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${status.bg} text-black`}>
              {status.text}
            </span>
          </div>
        </div>
        {!isComplete && (
          <div className={`flex items-center gap-2 ${isOverdue ? 'text-[#FF3B30]' : 'text-[#A1A1AA]'}`}>
            <Timer className="w-4 h-4" />
            <span className="text-sm font-mono">{timeRemaining.text}</span>
          </div>
        )}
      </div>

      <h3 className="text-white font-semibold text-lg mb-2">{task.title}</h3>
      <p className="text-[#A1A1AA] text-sm mb-4">{task.description}</p>

      <div className="flex flex-wrap items-center gap-4 text-sm text-[#52525B] mb-4">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4" />
          <span>{task.venue_name}</span>
        </div>
        {task.assigned_to_name && (
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>{task.assigned_to_name}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4" />
          <span>SLA: {task.sla_hours}h</span>
        </div>
      </div>

      {!isComplete && (
        <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#3F3F46]">
          {task.status === 'pending' && !isOverdue && (
            <Button
              onClick={() => handleStatusChange('in_progress')}
              disabled={updating}
              className="btn-rtb-secondary flex-1"
            >
              Start Working
            </Button>
          )}
          
          {task.status === 'in_progress' && (
             <Button
               onClick={() => handleStatusChange('completed')}
               disabled={updating}
               className="btn-rtb-primary flex-1"
             >
               <CheckCircle2 className="w-4 h-4 mr-2" />
               Mark Complete
             </Button>
          )}

          {isOverdue && task.status !== 'completed' && (
             <>
               {currentUser?.role === ROLES.OPS || currentUser?.role === ROLES.GM ? (
                 <Dialog>
                   <DialogTrigger asChild>
                     <Button variant="destructive" className="flex-1 bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90">
                       Ops Override
                     </Button>
                   </DialogTrigger>
                   <DialogContent className="bg-[#1E1E1E] border-[#3F3F46] text-white">
                     <DialogHeader>
                       <DialogTitle>Mandatory Incident Report</DialogTitle>
                     </DialogHeader>
                     <form onSubmit={handleOpsOverride} className="space-y-4 mt-4">
                       <p className="text-sm text-[#A1A1AA]">
                         Rescheduling an overdue task requires an explanatory note which will be permanently logged to the Venue Incident Reports.
                       </p>
                       <div>
                         <Label className="mb-2 block text-[#A1A1AA]">Reason for operational delay</Label>
                         <Input 
                           placeholder="Enter reason..."
                           value={overrideNote}
                           onChange={(e) => setOverrideNote(e.target.value)}
                           required
                           className="bg-[#121212] border-[#3F3F46] text-white"
                         />
                       </div>
                       <Button type="submit" className="w-full btn-rtb-primary" disabled={isSubmittingOverride}>
                         {isSubmittingOverride ? 'Logging...' : 'Confirm Override & Log'}
                       </Button>
                     </form>
                   </DialogContent>
                 </Dialog>
               ) : (
                 <Badge className="py-2 px-3 text-sm cursor-not-allowed flex-1 justify-center rounded-md bg-[#FF3B30]/20 text-[#FF3B30]">
                   Locked: Overdue (Contact Ops)
                 </Badge>
               )}
               
               <Button
                 onClick={() => handleStatusChange('completed')}
                 disabled={updating}
                 className="btn-rtb-primary flex-1"
               >
                 <CheckCircle2 className="w-4 h-4 mr-2" />
                 Force Complete
               </Button>
             </>
          )}

          {!task.assigned_to && !isOverdue && (
            <Button
              onClick={() => setShowAssign(true)}
              className="btn-rtb-secondary"
            >
              <User className="w-4 h-4 mr-2" />
              Assign
            </Button>
          )}
        </div>
      )}

      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="bg-[#1E1E1E] border-[#3F3F46] text-white">
          <DialogHeader>
            <DialogTitle>Assign Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-[#A1A1AA]">Select a team member to assign this task to:</p>
            {users.map(user => (
              <button
                key={user.id}
                onClick={() => handleAssign(user.id)}
                disabled={updating}
                className="w-full p-4 bg-[#27272A] hover:bg-[#3F3F46] border border-transparent hover:border-[#FF6600] rounded-md flex items-center gap-3 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-[#FF6600] flex items-center justify-center">
                  <span className="text-white font-bold">{user.name.charAt(0)}</span>
                </div>
                <div className="text-left">
                  <p className="text-white font-medium">{user.name}</p>
                  <p className="text-[#52525B] text-sm">{user.role}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TasksPage = () => {
  const { user } = useAuthStore();
  const [tasks, setTasks] = useState([]);
  const [venues, setVenues] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterVenue, setFilterVenue] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, venuesRes, usersRes] = await Promise.all([
        taskAPI.list(),
        venueAPI.list(),
        hasMinimumRole(user, ROLES.GM) ? userAPI.list() : Promise.resolve({ data: [] }),
      ]);
      setTasks(tasksRes.data);
      setVenues(venuesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      if (filterVenue !== 'all' && task.venue_id !== filterVenue) return false;
      if (filterStatus !== 'all' && task.status !== filterStatus) return false;
      return true;
    });
  }, [tasks, filterVenue, filterStatus]);

  const stats = useMemo(() => {
    return {
      total: tasks.length,
      pending: tasks.filter(t => t.status === 'pending').length,
      inProgress: tasks.filter(t => t.status === 'in_progress').length,
      overdue: tasks.filter(t => t.deadline && new Date(t.deadline) < new Date() && t.status !== 'completed').length,
      completed: tasks.filter(t => t.status === 'completed').length,
    };
  }, [tasks]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-[#121212]">
        <Loader2 className="w-8 h-8 text-[#FF6600] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
          Task Queue
        </h1>
        <p className="text-[#A1A1AA] mt-1">
          Manage and resolve operational tasks
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total', value: stats.total, color: 'text-white' },
          { label: 'Pending', value: stats.pending, color: 'text-[#FFB300]' },
          { label: 'In Progress', value: stats.inProgress, color: 'text-[#2979FF]' },
          { label: 'Overdue', value: stats.overdue, color: 'text-[#FF3B30]' },
          { label: 'Completed', value: stats.completed, color: 'text-[#00E676]' },
        ].map(stat => (
          <div key={stat.label} className="card-rtb text-center">
            <p className={`text-2xl font-bold font-heading ${stat.color}`}>{stat.value}</p>
            <p className="text-[#52525B] text-xs uppercase">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#A1A1AA]" />
          <span className="text-[#A1A1AA] text-sm">Filters:</span>
        </div>
        <Select value={filterVenue} onValueChange={setFilterVenue}>
          <SelectTrigger className="w-48 bg-[#27272A] border-[#3F3F46] text-white">
            <SelectValue placeholder="All Venues" />
          </SelectTrigger>
          <SelectContent className="bg-[#1E1E1E] border-[#3F3F46]">
            <SelectItem value="all" className="text-white">All Venues</SelectItem>
            {venues.map(venue => (
              <SelectItem key={venue.id} value={venue.id} className="text-white">
                {venue.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48 bg-[#27272A] border-[#3F3F46] text-white">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent className="bg-[#1E1E1E] border-[#3F3F46]">
            <SelectItem value="all" className="text-white">All Statuses</SelectItem>
            <SelectItem value="pending" className="text-white">Pending</SelectItem>
            <SelectItem value="in_progress" className="text-white">In Progress</SelectItem>
            <SelectItem value="completed" className="text-white">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredTasks.length > 0 ? (
        <div className="grid gap-4">
          {filteredTasks.map(task => (
            <TaskCard 
              key={task.id} 
              task={task} 
              onUpdate={fetchData}
              users={users.filter(u => u.venue_ids?.includes(task.venue_id))}
            />
          ))}
        </div>
      ) : (
        <div className="card-rtb text-center py-12">
          <AlertCircle className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">No Tasks</h3>
          <p className="text-[#A1A1AA]">
            {filterVenue !== 'all' || filterStatus !== 'all' 
              ? 'No tasks match your filters' 
              : 'All clear! No pending tasks.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default TasksPage;